import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, map, Observable} from 'rxjs';
import {MenuItem, MenuItemGroupKey, MORE_MENU_ITEMS, MENU_PATHS} from '../../../app.constants';

@Injectable({
  providedIn: 'root',
})
export class MenuRoutesService {
  private selectedMenuGroup$ = new BehaviorSubject<MenuItemGroupKey>(null);

  /**
   * Get the base menu items for each group (before permission filtering)
   */
  private getBaseMenuItemsForGroup(group: MenuItemGroupKey): MenuItem[] {
    switch (group) {
      case MenuItemGroupKey.More:
        return MORE_MENU_ITEMS;
      default:
        return [];
    }
  }

  /**
   * Set the currently selected menu group
   */
  setSelectedGroup(group: MenuItemGroupKey): void {
    this.selectedMenuGroup$.next(group);
  }

  /**
   * Clear the selected menu group
   */
  clearSelectedGroup(): void {
    this.selectedMenuGroup$.next(null);
  }

  /**
   * Get the currently selected menu group
   */
  getSelectedGroup$(): Observable<MenuItemGroupKey> {
    return this.selectedMenuGroup$.asObservable();
  }

  /**
   * Get filtered routes for an explicit list of items based on user permissions.
   * Used by ProjectsRoutingComponent to permission-filter its route items.
   */
  getFilteredRoutes$(items: MenuItem[], filteredMenuItems$: Observable<MenuItem[]>): Observable<MenuItem[]> {
    return filteredMenuItems$.pipe(
      map(filteredItems => items.filter(item => filteredItems.some(fi => fi.url === item.url)))
    );
  }

  /**
   * Get the filtered menu items for the selected group based on user permissions
   * This uses the same filtering logic as the original getBuildRoutes method
   */
  getRoutesForSelectedGroup$(filteredMenuItems$: Observable<MenuItem[]>, isMobile?: boolean): Observable<MenuItem[]> {
    return combineLatest([this.selectedMenuGroup$, filteredMenuItems$]).pipe(
      map(([selectedGroup, filteredItems]) => {
        if (!selectedGroup) return [];
        const isMobilePlatform = !!isMobile;
        const groupItems = this.getBaseMenuItemsForGroup(selectedGroup);
        const filteredGroupItems = groupItems
          .filter(groupItem => filteredItems.some(filteredItem => filteredItem.url === groupItem.url))
          .filter(
            groupItem =>
              !(
                selectedGroup === MenuItemGroupKey.More &&
                groupItem.url === MENU_PATHS.NotificationSettings &&
                !isMobilePlatform
              )
          );

        if (isMobilePlatform && selectedGroup === MenuItemGroupKey.More) {
          filteredGroupItems.push({
            display: 'Logout',
            icon: 'logout',
            itemGroup: MenuItemGroupKey.More,
            meta: {isLogout: true},
          } as MenuItem);
        }
        return filteredGroupItems;
      })
    );
  }
}
